import { z } from "zod";
import {
  BRAZIL_UFS,
  FISCAL_FILE_KINDS,
  MEI_ACTIVITIES,
  PACKAGE_CONTENTS,
  isCompetence,
  isValidCNPJ,
  isValidCPF,
  type FiscalFileKind,
  type MeiActivity,
  type PackageContent,
} from "@cx/domain";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Espaço Meu Contador, arquivos fiscais e DAS MEI (T3.5, RF-CTB-01/02, RF-PRF-10) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const trimmed = (max: number, what: string) =>
  z
    .string()
    .trim()
    .max(max, `${what}: até ${max} caracteres`)
    .transform((s) => s || null);
const digits = (s: string) => s.replace(/\D/g, "");

const competence = z
  .string("Informe a competência")
  .trim()
  .refine(isCompetence, "Competência inválida (use AAAA-MM)");

const emailField = z
  .string("Informe o e-mail")
  .trim()
  .toLowerCase()
  .pipe(z.email("E-mail inválido").max(254));

export const accountantAddress = z.object({
  cep: nullish(
    z
      .string()
      .transform(digits)
      .pipe(z.string().regex(/^(\d{8})?$/, "CEP: 8 dígitos"))
      .transform((s) => s || null),
  ),
  street: nullish(trimmed(60, "Endereço")),
  number: nullish(trimmed(10, "Número")),
  complement: nullish(trimmed(60, "Complemento")),
  district: nullish(trimmed(60, "Bairro")),
  cityIbge: nullish(
    z
      .string()
      .transform(digits)
      .pipe(z.string().regex(/^(\d{7})?$/, "Código IBGE do município: 7 dígitos"))
      .transform((s) => s || null),
  ),
  cityName: nullish(trimmed(60, "Município")),
  uf: nullish(z.enum(BRAZIL_UFS, "UF inválida")),
});
export type AccountantAddress = z.output<typeof accountantAddress>;

/** Passo 1: o contador (vai no registro 0100 do SPED). */
export const accountantInput = z.object({
  name: z
    .string("Informe o nome")
    .trim()
    .min(3, "Nome: ao menos 3 caracteres")
    .max(100, "Nome: até 100 caracteres"),
  cpf: z
    .string("Informe o CPF do contador")
    .transform(digits)
    .refine((s) => isValidCPF(s), "CPF inválido"),
  crc: z
    .string("Informe o CRC")
    .trim()
    .toUpperCase()
    .min(4, "CRC: ao menos 4 caracteres")
    .max(15, "CRC: até 15 caracteres")
    .regex(/^[0-9A-Z./-]+$/, "CRC: só letras, números, ponto, barra e hífen"),
  cnpj: nullish(
    z
      .string()
      .transform(digits)
      .refine((s) => s === "" || isValidCNPJ(s), "CNPJ do escritório inválido")
      .transform((s) => s || null),
  ),
  email: emailField,
  phone: nullish(
    z
      .string()
      .transform(digits)
      .pipe(z.string().regex(/^(\d{10,11})?$/, "Telefone: DDD + número"))
      .transform((s) => s || null),
  ),
  address: accountantAddress.default({
    cep: null,
    street: null,
    number: null,
    complement: null,
    district: null,
    cityIbge: null,
    cityName: null,
    uf: null,
  }),
});
export type AccountantInput = z.output<typeof accountantInput>;

/** Passo 2: conteúdo do pacote. */
export const packageContentsInput = z.object({
  contents: z
    .array(z.enum(PACKAGE_CONTENTS, "Conteúdo inválido"))
    .min(1, "Escolha ao menos um item do pacote")
    .max(PACKAGE_CONTENTS.length)
    .transform((a) => PACKAGE_CONTENTS.filter((c) => a.includes(c))),
});

/** Passo 3: envio automático (dia 1–28) ou desligado. */
export const packageScheduleInput = z.object({
  autoDay: z.number().int().min(1, "Dia entre 1 e 28").max(28, "Dia entre 1 e 28").nullable(),
});

export const spedSettingsInput = z.object({
  profile: z.enum(["A", "B", "C"]).default("A"),
  icmsRevenueCode: nullish(
    z
      .string()
      .trim()
      .max(20, "Código de receita: até 20 caracteres")
      .regex(/^[0-9A-Za-z./-]*$/, "Código de receita: só letras, números, ponto, barra e hífen")
      .transform((s) => s || null),
  ),
  icmsDueDay: z.number().int().min(1).max(31).default(10),
  /** Mês em que o inventário (bloco H / registro 74) sai: 2 = fevereiro (31/12 do ano anterior); null = nunca. */
  inventoryMonth: z.number().int().min(1).max(12).nullable().default(2),
});
export type SpedSettingsInput = z.output<typeof spedSettingsInput>;

/** Gravação da configuração: cada seção é opcional (o assistente grava um passo por vez). */
export const accountantSettingsInput = z
  .object({
    accountant: accountantInput.optional(),
    package: packageContentsInput.optional(),
    schedule: packageScheduleInput.optional(),
    sped: spedSettingsInput.optional(),
    meiActivity: z.enum(MEI_ACTIVITIES, "Atividade inválida").nullable().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nada para gravar");
export type AccountantSettingsInput = z.output<typeof accountantSettingsInput>;

export type SpedSettings = {
  profile: "A" | "B" | "C";
  icmsRevenueCode: string | null;
  icmsDueDay: number;
  inventoryMonth: number | null;
};

export type AccountantSettingsView = {
  accountant: {
    name: string;
    cpf: string;
    crc: string;
    cnpj: string | null;
    email: string;
    phone: string | null;
    address: AccountantAddress;
  } | null;
  contents: PackageContent[];
  autoDay: number | null;
  nextRunOn: string | null;
  lastAutoCompetence: string | null;
  sped: SpedSettings;
  meiActivity: MeiActivity | null;
  /** Passos concluídos do assistente (1 contador, 2 conteúdo, 3 envio). */
  setupStep: number;
  company: { legalName: string; cnpj: string | null; crt: number; taxRegime: string | null; isMei: boolean };
};

// ---------- arquivos fiscais ----------

export const fiscalFileInput = z
  .object({
    kind: z.enum(FISCAL_FILE_KINDS, "Tipo de arquivo inválido"),
    competence,
    /** SPED: 0 original, 1 substituto. Sintegra: 1 normal, 2 retificação total, 3 aditiva, 5 desfazimento. */
    purpose: z.number().int().optional(),
    /** Inventário no arquivo (null = pelo mês configurado). */
    withInventory: z.boolean().nullable().default(null),
    /** Data do inventário (padrão: 31/12 do ano anterior). */
    inventoryDate: nullish(businessDate),
    /** Saldo credor de ICMS do mês anterior (null = o do último SPED pronto do mês anterior). */
    previousCreditCents: z.number().int().min(0).max(99_999_999_999).nullable().default(null),
  })
  .transform((v) => ({
    ...v,
    purpose: v.purpose ?? (v.kind === "sintegra" ? 1 : 0),
  }))
  .refine((v) => (v.kind === "sintegra" ? [1, 2, 3, 5].includes(v.purpose) : [0, 1].includes(v.purpose)), {
    message: "Finalidade inválida para o tipo de arquivo",
    path: ["purpose"],
  });
export type FiscalFileInput = z.output<typeof fiscalFileInput>;

export const fiscalFileListQuery = z.object({
  kind: z.enum(FISCAL_FILE_KINDS).optional(),
  competence: competence.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type FiscalFileListQuery = z.output<typeof fiscalFileListQuery>;

export type FiscalFileStatus = "queued" | "ready" | "failed";
export type FiscalFileSummary = {
  counts: Record<string, number>;
  totals: Record<string, number | string>;
  warnings: string[];
};
export type FiscalFileView = {
  id: string;
  kind: FiscalFileKind;
  competence: string;
  periodStart: string;
  periodEnd: string;
  purpose: number;
  status: FiscalFileStatus;
  jobId: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  recordsCount: number | null;
  summary: FiscalFileSummary | null;
  error: string | null;
  createdAt: string;
  createdBy: string | null;
};
export type FiscalFileList = { data: FiscalFileView[]; nextCursor: string | null };

// ---------- pacotes ----------

export const accountantPackageInput = z.object({
  competence,
  /** null = o conteúdo configurado no passo 2. */
  contents: z
    .array(z.enum(PACKAGE_CONTENTS))
    .min(1, "Escolha ao menos um item do pacote")
    .max(PACKAGE_CONTENTS.length)
    .nullable()
    .default(null),
  /** Enviar ao contador assim que montar. */
  send: z.boolean().default(false),
});
export type AccountantPackageInput = z.output<typeof accountantPackageInput>;

export const packageListQuery = z.object({
  direction: z.enum(["sent", "received", "all"]).default("all"),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type PackageListQuery = z.output<typeof packageListQuery>;

/** Recebido (multipart): competência e descrição; o arquivo vai no campo `file`. */
export const receivedInput = z.object({
  competence,
  description: z
    .string("Descreva o arquivo")
    .trim()
    .min(3, "Descrição: ao menos 3 caracteres")
    .max(200, "Descrição: até 200 caracteres"),
});
export type ReceivedInput = z.output<typeof receivedInput>;
export const RECEIVED_MAX_BYTES = 10 * 1024 * 1024;

export type PackageStatus = "queued" | "ready" | "sending" | "sent" | "failed";
export type PackageView = {
  id: string;
  number: number;
  direction: "sent" | "received";
  competence: string;
  contents: PackageContent[];
  status: PackageStatus;
  jobId: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  manifest: {
    files: { path: string; bytes: number }[];
    counts: Record<string, number>;
    warnings: string[];
  } | null;
  description: string | null;
  sendOnReady: boolean;
  sentTo: string | null;
  sentAt: string | null;
  sendCount: number;
  emailMode: "attached" | "notice" | null;
  error: string | null;
  auto: boolean;
  createdAt: string;
  createdBy: string | null;
};
export type PackageList = { data: PackageView[]; nextCursor: string | null };

export type AccountingFileLink = { url: string; fileName: string };

// ---------- DAS MEI ----------

export const DAS_STATUSES = ["pending", "issued", "paid", "canceled"] as const;
export type DasStatus = (typeof DAS_STATUSES)[number];

export const dasGenerateInput = z.object({ competence });
export const dasListQuery = z.object({
  year: z.coerce.number().int().min(2023).max(2100).optional(),
  status: z.enum([...DAS_STATUSES, "all"]).default("all"),
});
export type DasListQuery = z.output<typeof dasListQuery>;
export const dasIssueInput = z.object({
  barcode: z
    .string("Informe o código de barras ou a linha digitável")
    .trim()
    .min(1, "Informe o código de barras ou a linha digitável")
    .max(80, "Código de barras inválido"),
});
export const dasPayInput = z.object({
  paidAt: businessDate,
  financialAccountId: ulidParam,
  paymentMethodId: nullish(ulidParam),
  interestCents: z.number().int().min(0).max(99_999_999_999).default(0),
  fineCents: z.number().int().min(0).max(99_999_999_999).default(0),
});
export type DasPayInput = z.output<typeof dasPayInput>;
/** Pagamento pelo provedor: a baixa cai nesta conta quando o provedor confirmar. */
export const dasProviderPayInput = z.object({ financialAccountId: ulidParam });
export const dasCancelInput = z.object({
  reason: z.string("Informe o motivo").trim().min(5, "Motivo: ao menos 5 caracteres").max(255),
});
/** Liberar pagamento "a conferir": o usuário diz o que conferiu no provedor (fica na trilha). */
export const dasReleaseInput = z.object({
  reason: z
    .string("Informe o que foi conferido")
    .trim()
    .min(10, "Descreva o que conferiu (ao menos 10 caracteres)")
    .max(255),
});

export type DasView = {
  id: string;
  competence: string;
  activity: MeiActivity;
  inssCents: number;
  icmsCents: number;
  issCents: number;
  totalCents: number;
  dueDate: string;
  overdue: boolean;
  status: DasStatus;
  barcode: string | null;
  digitableLine: string | null;
  payableId: string | null;
  paymentProvider: string | null;
  paymentStatus: "requested" | "submitting" | "paid" | "failed" | "unknown" | null;
  paymentError: string | null;
  paidAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  pgmeiUrl: string;
};
export type DasOverview = {
  isMei: boolean;
  activity: MeiActivity | null;
  /** Prévia da competência atual (sem gravar). */
  preview: {
    competence: string;
    inssCents: number;
    icmsCents: number;
    issCents: number;
    totalCents: number;
    dueDate: string;
  } | null;
  data: DasView[];
  pgmeiUrl: string;
};
